
import { useState, useMemo } from 'react';
import MarkdownBlock from '../LazyMarkdownBlock';
import { MAX_TITLE_LENGTH } from '../../constants';
import TagEditor from '../TagEditor';
import { ASSET_USAGE_STATUS_LABELS } from '../../lib/assetUsage';

export default function AssetDetail({
  selectedAsset,
  setSelectedAssetId,
  handleUpdateAssetTitle,
  handleUpdateAssetBody,
  handleUpdateAssetType,
  handleUpdateAssetTags,
  getAssetTypeLabel,
  ASSET_TYPE_DESCRIPTIONS,
  ASSET_TYPE_LABELS,
  ASSET_STATUS_LABELS,
  handleUpdateAssetStatus,
  handleUpdateAssetMaturity,
  getAssetStatusLabel,
  jumpToMap,
  handleDiscussInSink,
  insights = [],
  notes = [],
  chatRooms = [],
  onReferenceClick,
  openSourceRoom,
  tagSuggestions = [],
  showToast,
}) {
  const [bodyCopied, setBodyCopied] = useState(false);
  const [localTitle, setLocalTitle] = useState(selectedAsset?.title || '');
  const [isBodyEditing, setIsBodyEditing] = useState(false);
  const [localBody, setLocalBody] = useState(selectedAsset?.body || '');

  // Track the ID of the asset we're currently displaying to reset local state when it changes
  const [lastSelectedId, setLastSelectedId] = useState(selectedAsset?.id);

  if (selectedAsset?.id !== lastSelectedId) {
    setLastSelectedId(selectedAsset?.id);
    setLocalTitle(selectedAsset?.title || '');
    setLocalBody(selectedAsset?.body || '');
    setIsBodyEditing(false);
  }

  const handleTitleBlur = () => {
    if (localTitle !== selectedAsset?.title) {
      handleUpdateAssetTitle(selectedAsset?.id, localTitle);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const sourceInsights = useMemo(() => {
    if (!selectedAsset) return [];
    const ids = selectedAsset?.sourceInsightIds || (selectedAsset?.sourceInsightId ? [selectedAsset?.sourceInsightId] : []);
    return ids
      .map(id => insights.find(i => i.id === id))
      .filter(Boolean)
      .filter(i => i.status === 'accepted');
  }, [selectedAsset, insights]);

  const sourceNotes = useMemo(() => {
    if (!selectedAsset) return [];
    const ids = selectedAsset?.sourceNoteIds || (selectedAsset?.sourceNoteId ? [selectedAsset?.sourceNoteId] : []);
    return ids.map(id => notes.find(n => n.id === id)).filter(Boolean);
  }, [selectedAsset, notes]);

  const sourceRooms = useMemo(() => {
    if (!selectedAsset) return [];
    const ids = selectedAsset?.sourceRoomIds || (selectedAsset?.sourceRoomId ? [selectedAsset?.sourceRoomId] : []);
    return ids.map(id => chatRooms.find(r => r.id === id)).filter(Boolean);
  }, [selectedAsset, chatRooms]);

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('ja-JP');
    } catch {
      return '';
    }
  };

  const handleCopyBody = () => {
    navigator.clipboard.writeText(selectedAsset?.body || '');
    setBodyCopied(true);
    setTimeout(() => setBodyCopied(false), 2000);
  };

  if (!selectedAsset) return null;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col px-4 pb-4 space-y-4 relative">
      <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur py-2 -mx-4 px-4 flex justify-between items-center border-b border-gray-800/50">
        <button onClick={() => setSelectedAssetId(null)} className="text-sm text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
          一覧
        </button>
        <div className="flex items-center gap-1.5">
          {handleDiscussInSink && (
            <button
              onClick={() => handleDiscussInSink('concept', selectedAsset?.id, selectedAsset?.title)}
              title="Sinkで議論"
              className="p-2 rounded-lg border border-indigo-700/50 bg-indigo-950/40 text-indigo-400 hover:bg-indigo-600 hover:text-white hover:border-indigo-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </button>
          )}
          {jumpToMap && (
            <button
              onClick={() => jumpToMap(`asset:${selectedAsset?.id}`, selectedAsset?.title)}
              title="Mapで確認"
              className="p-2 rounded-lg border border-slate-700/50 bg-slate-900/40 text-slate-400 hover:bg-slate-700 hover:text-white hover:border-slate-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A2 2 0 013 15.488V5.113a2 2 0 011.134-1.789L9 1m0 19l6 3m-6-3V1m6 22l5.447-2.724A2 2 0 0021 18.512V8.113a2 2 0 00-1.134-1.789L15 4m0 19V4m0 0L9 1" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <article className="space-y-6">
        <>
          <header>
              <div className="flex items-center gap-2 mb-1.5 overflow-x-auto no-scrollbar">
                <div className="relative flex-shrink-0">
                  <select
                    value={selectedAsset?.type}
                    onChange={(e) => handleUpdateAssetType(selectedAsset?.id, e.target.value)}
                    className="appearance-none text-[10px] font-bold text-emerald-400 uppercase tracking-widest bg-emerald-950/20 border border-emerald-900/50 px-2 py-0.5 rounded pr-6 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 cursor-pointer"
                  >
                    {Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value} className="bg-gray-900 text-gray-200">{label}</option>
                    ))}
                  </select>
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-emerald-600">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>

                <div className="relative flex-shrink-0">
                  <select
                    value={selectedAsset?.status}
                    onChange={(e) => handleUpdateAssetStatus(selectedAsset?.id, e.target.value)}
                    className="appearance-none text-[9px] font-bold uppercase tracking-widest text-gray-400 bg-gray-800/40 border border-gray-700/50 px-2 py-0.5 rounded pr-6 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 cursor-pointer"
                  >
                    {Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value} className="bg-gray-900 text-gray-200">{label}</option>
                    ))}
                  </select>
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 group/title">
                <input
                  type="text"
                  value={localTitle}
                  onChange={(e) => setLocalTitle(e.target.value)}
                  onBlur={handleTitleBlur}
                  onKeyDown={handleTitleKeyDown}
                  maxLength={MAX_TITLE_LENGTH}
                  className="flex-1 bg-transparent text-xl font-bold text-white leading-tight focus:outline-none focus:ring-1 focus:ring-emerald-500/30 rounded px-1 -ml-1 border-none"
                  placeholder="タイトルを入力..."
                />
                <svg className="w-3.5 h-3.5 text-gray-600 opacity-0 group-hover/title:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <div className="mt-3">
                <TagEditor
                  tags={selectedAsset?.tags || []}
                  onUpdate={(newTags) => handleUpdateAssetTags(selectedAsset?.id, newTags)}
                  suggestions={tagSuggestions}
                  showToast={showToast}
                />
              </div>
            </header>

            <section className="bg-emerald-900/10 border border-emerald-800/30 rounded-xl p-3 shadow-inner">
              <p className="text-[10px] text-emerald-400 font-black uppercase tracking-widest mb-1.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                {getAssetTypeLabel(selectedAsset?.type)}の使い道
              </p>
              <p className="text-[11px] text-gray-400 leading-relaxed font-medium">{ASSET_TYPE_DESCRIPTIONS[selectedAsset?.type] || ASSET_TYPE_DESCRIPTIONS['knowledge']}</p>
            </section>

            <section className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4 relative group/section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500">定義・内容</h3>
                    <button
                      onClick={() => {
                        if (isBodyEditing && localBody !== selectedAsset?.body) {
                          handleUpdateAssetBody(selectedAsset?.id, localBody);
                        }
                        setIsBodyEditing(!isBodyEditing);
                      }}
                      className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-gray-300 transition-all"
                      title={isBodyEditing ? "プレビュー" : "編集"}
                    >
                      {isBodyEditing ? (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      )}
                    </button>
                  </div>
                </div>
                <button 
                  onClick={handleCopyBody}
                  className="text-[10px] font-bold text-gray-500 hover:text-gray-200 bg-gray-800/40 hover:bg-gray-800 border border-gray-700/50 px-2 py-0.5 rounded transition-all flex items-center gap-1 opacity-80 hover:opacity-100"
                  title="定義・内容をコピー"
                >
                  {bodyCopied ? (
                    <>
                      <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                      <span className="text-emerald-400">コピーしました</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path></svg>
                      <span>コピー</span>
                    </>
                  )}
                </button>
              </div>
              <div className="text-gray-300 text-[13px]">
                {isBodyEditing ? (
                  <textarea
                    value={localBody}
                    onChange={(e) => setLocalBody(e.target.value)}
                    onBlur={() => {
                      if (localBody !== selectedAsset?.body) {
                        handleUpdateAssetBody(selectedAsset?.id, localBody);
                      }
                    }}
                    rows={10}
                    className="w-full bg-gray-900/50 border border-gray-700/50 rounded-lg p-3 text-xs font-mono text-gray-200 focus:outline-none focus:border-emerald-500/50 leading-relaxed resize-y"
                    placeholder="内容を入力..."
                  />
                ) : (
                  <MarkdownBlock text={selectedAsset?.body} />
                )}
              </div>
            </section>

            <section className="space-y-4">
              {(selectedAsset?.sourceNoteIds?.length > 0 || !!selectedAsset?.sourceNoteId) && (
                <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4">
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-3">由来</h3>
                  <div className="space-y-3">
                    {sourceNotes.length > 0 && (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tighter">由来Note:</span>
                        <div className="flex flex-wrap gap-2">
                          {sourceNotes.map(note => (
                            <button
                              key={note.id}
                              onClick={() => onReferenceClick?.('note', note.id)}
                              className="text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1 bg-blue-950/20 border border-blue-900/30 px-2 py-1 rounded-lg"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                              {note.title || '(無題のNote)'}
                              <span className="text-[10px] font-medium opacity-60 ml-0.5">{formatDate(note.createdAt)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {sourceInsights.length > 0 && (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tighter">由来Hint:</span>
                        <div className="flex flex-wrap gap-2">
                          {sourceInsights.map(insight => (
                            <button
                              key={insight.id}
                              onClick={() => onReferenceClick?.('insight', insight.id)}
                              className="text-xs font-bold text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1 bg-purple-950/20 border border-purple-900/30 px-2 py-1 rounded-lg"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                              {insight.title || '(無題のHint)'}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {sourceRooms.length > 0 && (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tighter">由来Room:</span>
                        <div className="flex flex-wrap gap-2">
                          {sourceRooms.map(room => (
                            <button
                              key={room.id}
                              onClick={() => openSourceRoom?.(room.id)}
                              className="text-xs font-bold text-indigo-400 hover:text-indigo-300 transition-colors flex items-center gap-1 bg-indigo-950/20 border border-indigo-900/30 px-2 py-1 rounded-lg"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                              {room.title || '(無題のRoom)'}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-3">状態制御</h3>
                <div className="space-y-3">
                  <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Usage</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">{ASSET_USAGE_STATUS_LABELS[selectedAsset?.usageStatus] || selectedAsset?.usageStatus || 'new'}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-[11px] text-gray-300">
                      <div className="rounded bg-gray-800/70 border border-gray-700/50 px-2 py-1.5">
                        <p className="text-[9px] uppercase tracking-widest text-gray-500">累計参照回数</p>
                        <p className="font-bold text-white">{selectedAsset?.referenceCount || 0}</p>
                      </div>
                      <div className="rounded bg-gray-800/70 border border-gray-700/50 px-2 py-1.5">
                        <p className="text-[9px] uppercase tracking-widest text-gray-500">直近30日参照</p>
                        <p className="font-bold text-white">{selectedAsset?.referencesLast30Days || 0}</p>
                      </div>
                      <div className="rounded bg-gray-800/70 border border-gray-700/50 px-2 py-1.5">
                        <p className="text-[9px] uppercase tracking-widest text-gray-500">直近参照日</p>
                        <p className="font-bold text-white">{formatDate(selectedAsset?.lastReferencedAt) || '未参照'}</p>
                      </div>
                      <div className="rounded bg-gray-800/70 border border-gray-700/50 px-2 py-1.5">
                        <p className="text-[9px] uppercase tracking-widest text-gray-500">未参照経過日数</p>
                        <p className="font-bold text-white">{typeof selectedAsset?.daysSinceLastReference === 'number' ? `${selectedAsset.daysSinceLastReference}日` : '-'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tighter w-16">Status:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {['active', 'pending', 'draft', 'done', 'archived'].map(s => (
                        <button 
                          key={s} 
                          onClick={() => handleUpdateAssetStatus(selectedAsset?.id, s)}
                          className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-all ${selectedAsset?.status === s ? 'bg-emerald-600 text-white border-emerald-500 shadow-md shadow-emerald-900/20' : 'bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700'}`}
                        >
                          {getAssetStatusLabel(s)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold text-gray-700 uppercase tracking-tighter w-16">Maturity:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {['seed', 'growing', 'crystallized'].map(m => (
                        <button 
                          key={m} 
                          onClick={() => handleUpdateAssetMaturity(selectedAsset?.id, m)}
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all ${selectedAsset?.maturity === m ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-gray-900 text-gray-500 border-gray-800 hover:bg-gray-800'}`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
      </article>
    </div>
  );
}
